import React, { useState, useEffect } from "react";
import AuthLayout from "../auth/AuthLayout";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc, getFirestore } from "firebase/firestore";
import { transferFunds } from "../../services/walletService";
import { Loader } from "lucide-react";
import UserSignInIcon from "../../assets/auth/user-sign-in.svg";
import { createNotification } from "../../store/slices/notificationSlice";
import { useDispatch, useAppDispatch } from "../../store/hooks";
import paymentLogo from "../../assets/image/paymentlogo.png";
interface FundRequestData {
  id: string;
  requesterId: string;
  requesterName: string;
  requesterEmail: string;
  orgId: string;
 
  amount: number;
  status: "pending" | "approved" | "rejected";
  createdAt: any;
  notes: string;
  message: string;
  ownerUid: string;
}

const FundRequest = () => {
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("request_id");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [fundRequest, setFundRequest] = useState<FundRequestData | null>(null);
  const [approvingAction, setApprovingAction] = useState<boolean>(false);
  const [decliningAction, setDecliningAction] = useState<boolean>(false);
  const [actionComplete, setActionComplete] = useState<boolean>(false);
  const [actionMessage, setActionMessage] = useState<string>("");

  const db = getFirestore();
  console.log(fundRequest, "fundRequest");
  useEffect(() => {
    const fetchFundRequest = async () => {
      if (!requestId) {
        setError("No request ID provided");
        setLoading(false);
        return;
      }

      try {
        const fundRequestRef = doc(db, "fundRequests", requestId);
        const fundRequestDoc = await getDoc(fundRequestRef);

        if (fundRequestDoc.exists()) {
          const requestData = fundRequestDoc.data() as FundRequestData;
          console.log("Fund request data loaded:", requestData);
          // Make sure ownerUid is available
          if (!requestData.ownerUid && requestData.ownerUid) {
            requestData.ownerUid = requestData.ownerUid;
          }
          setFundRequest(requestData);
        } else {
          setError("Fund request not found");
        }
      } catch (err) {
        console.error("Error fetching fund request:", err);
        setError("Failed to load fund request details");
      } finally {
        setLoading(false);
      }
    };

    fetchFundRequest();
  }, [requestId, db]);

  const handleAction = async (action: "approve" | "decline") => {
    if (!requestId || !fundRequest) return;

    if (action === "approve") {
      setApprovingAction(true);
    } else {
      setDecliningAction(true);
    }

    try {
      // If we're approving, first check if there are sufficient funds
      if (action === "approve") {
        const fromWalletId = fundRequest.ownerUid; // Organization wallet
        const toWalletId = fundRequest.requesterId; // Requester's wallet
        const amount = fundRequest.amount;
        const transferResult = await transferFunds(
          fromWalletId,
          toWalletId,
          amount,
          fundRequest.ownerUid || fundRequest.ownerUid || "Owner"
        );

        if (!transferResult.success) {
          if (transferResult.error?.includes("Insufficient balance")) {
            throw new Error(
              `Your organization doesn't have enough funds to approve this request. Please add funds to your organization wallet first.`
            );
          } else if (
            transferResult.error?.includes("Source wallet not found")
          ) {
            throw new Error(
              `The organization wallet could not be found. Please contact support.`
            );
          } else if (
            transferResult.error?.includes("Destination wallet not found")
          ) {
            throw new Error(
              `The recipient's wallet could not be found. Please contact support.`
            );
          } else {
            throw new Error(
              `Failed to transfer funds: ${transferResult.error}`
            );
          }
        }

        // Create notification for the requester with meta
        try {
          const requesterNotificationPayload = {
            userId: fundRequest.requesterId,
            userEmail: fundRequest.requesterEmail,
            message: `Your fund request of $${amount.toFixed(
              2
            )} has been approved`,
            trigger: "fund_request_approved",
            triggeredBy: fundRequest.ownerUid || "system",
            meta: {
              requestId: requestId,
              amount: amount,
              requesterUid: fundRequest.requesterId,
              requesterEmail: fundRequest.requesterEmail,
              status: "approved",
              approvedAt: new Date().toISOString(),
              approvedBy: fundRequest.ownerUid || fundRequest.ownerUid,
              approverName: "Organization Owner",
            },
          };
          await dispatch(
            createNotification(requesterNotificationPayload)
          ).unwrap();

          // Create notification for the owner/admin with meta
          if (fundRequest.ownerUid) {
            const ownerNotificationPayload = {
              userId: fundRequest.ownerUid,
              userEmail: fundRequest.ownerEmail || "",
              message: `You approved a fund request of $${amount.toFixed(
                2
              )} for ${fundRequest.requesterEmail}`,
              trigger: "fund_request_approved",
              triggeredBy: fundRequest.ownerUid,
              meta: {
                requestId: requestId,
                amount: amount,
                requesterUid: fundRequest.requesterId,
                requesterEmail: fundRequest.requesterEmail,
                status: "approved",
                approvedAt: new Date().toISOString(),
                approvedBy: fundRequest.ownerUid,
                approverName: "Organization Owner",
              },
            };
            await dispatch(
              createNotification(ownerNotificationPayload)
            ).unwrap();
          }
        } catch (notificationError) {
          console.error(
            "Failed to create fund request notifications:",
            notificationError
          );
        }
      } else if (action === "decline") {
        // Create notification for the requester with meta
        try {
          const declineNotificationPayload = {
            userId: fundRequest.requesterId,
            userEmail: fundRequest.requesterEmail,
            message: `Your fund request of $${fundRequest.amount.toFixed(
              2
            )} has been rejected`,
            trigger: "fund_request_rejected",
            triggeredBy: fundRequest.ownerUid || "",
            meta: {
              requestId: requestId,
              amount: fundRequest.amount,
              requesterUid: fundRequest.requesterId,
              requesterEmail: fundRequest.requesterEmail,
              status: "rejected",
              rejectedAt: new Date().toISOString(),
              rejectedBy: fundRequest.ownerUid || fundRequest.ownerUid,
              rejectorName: "Organization Owner",
            },
          };
          await dispatch(
            createNotification(declineNotificationPayload)
          ).unwrap();

          // Create notification for the owner/admin with meta
          if (fundRequest.ownerUid) {
            const ownerDeclineNotificationPayload = {
              userId: fundRequest.ownerUid,
              userEmail: fundRequest.ownerEmail || "",
              message: `You rejected a fund request of $${fundRequest.amount.toFixed(
                2
              )} for ${fundRequest.requesterEmail}`,
              trigger: "fund_request_rejected",
              triggeredBy: fundRequest.ownerUid,
              meta: {
                requestId: requestId,
                amount: fundRequest.amount,
                requesterUid: fundRequest.requesterId,
                requesterEmail: fundRequest.requesterEmail,
                status: "rejected",
                rejectedAt: new Date().toISOString(),
                rejectedBy: fundRequest.ownerUid,
                rejectorName: "Organization Owner",
              },
            };
            await dispatch(
              createNotification(ownerDeclineNotificationPayload)
            ).unwrap();
          }
        } catch (notificationError) {
          console.error(
            "Failed to create fund request rejection notification:",
            notificationError
          );
        }
      }

      // Update the fund request status
      const fundRequestRef = doc(db, "fundRequests", requestId);
      const updateData = {
        status: action === "approve" ? "approved" : "rejected",
        updatedAt: new Date(),
        isRead: true,
        ...(action === "approve"
          ? { approvedAt: new Date() }
          : { rejectedAt: new Date() }),
      };
      setError("");
      await updateDoc(fundRequestRef, updateData);

      setFundRequest({
        ...fundRequest,
        status: action === "approve" ? "approved" : "rejected",
      });

      setActionComplete(true);
      setActionMessage(
        action === "approve"
          ? `Fund request for $${fundRequest.amount.toFixed(
              2
            )} has been approved and funds have been transferred.`
          : `Fund request for $${fundRequest.amount.toFixed(
              2
            )} has been declined.`
      );
    } catch (err) {
      console.error(
        `Error ${
          action === "approve" ? "approving" : "declining"
        } fund request:`,
        err
      );
      setError(
        err.message || `Failed to ${action} the fund request. Please try again.`
      );
    } finally {
      if (action === "approve") {
        setApprovingAction(false);
      } else {
        setDecliningAction(false);
      }
    }
  };

  const handleSignIn = () => {
    navigate("/signin");
  };

  return (
    <AuthLayout>
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-row w-full shadow-lg rounded-lg overflow-hidden h-[100vh]">
          <div className="flex-1 flex flex-col justify-center p-8 bg-white space-y-6 items-center">
            <h1 className="text-2xl font-bold mb-6">Fund Request Details</h1>

            {loading && (
              <div className="flex flex-col items-center justify-center mt-10 py-8">
                <Loader className="h-10 w-10 text-[#F15D18] animate-spin mb-4" />
                <p className="text-[#696969] nunito-regular text-lg">
                  Loading Fund Request details...
                </p>
              </div>
            )}

            {error && (
              <div
                className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative my-4"
                role="alert"
              >
                <span className="block sm:inline">{error}</span>
              </div>
            )}

            {actionComplete && (
              <div className="flex flex-col items-center space-y-6">
                <div
                  className={`${
                    fundRequest?.status === "approved"
                      ? "bg-green-100 border-green-400 text-green-700"
                      : "bg-blue-100 border-blue-400 text-blue-700"
                  } px-4 py-3 rounded relative my-4 w-full`}
                  role="alert"
                >
                  <span className="block sm:inline">{actionMessage}</span>
                </div>
                <button
                  onClick={handleSignIn}
                  className="flex items-center justify-center bg-[#F15D18] hover:bg-[#E54D08] text-white font-medium py-2 px-8 rounded focus:outline-none focus:ring-2 focus:ring-[#F15D18] focus:ring-opacity-50 w-full max-w-xs"
                >
                  Sign In
                </button>
              </div>
            )}

            {fundRequest && !loading && !actionComplete && (
              <div className="bg-white rounded-lg shadow-md p-6 mt-4">
                <div className="mb-6">
                  <h2 className="text-gray-600 text-lg font-semibold">
                    Request Information
                  </h2>
                  <p className="text-3xl font-bold mb-3">
                    ${fundRequest.amount.toFixed(2)}
                  </p>
                  <p className="mb-2">
                    <span className="font-semibold">Status:</span>{" "}
                    {fundRequest.status.charAt(0).toUpperCase() +
                      fundRequest.status.slice(1)}
                  </p>
                  <p className="mb-2">
                    <span className="font-semibold">Requested by:</span>{" "}
                    {fundRequest.requesterName}
                  </p>
                  <p className="mb-2">
                    <span className="font-semibold">Email:</span>{" "}
                    {fundRequest.requesterEmail}
                  </p>
                  <p className="mb-2">
                    <span className="font-semibold">Date:</span>{" "}
                    {fundRequest.createdAt?.toDate().toLocaleDateString() ||
                      "N/A"}
                  </p>
                </div>

                {fundRequest.status === "pending" && (
                  <div className="mt-6 flex gap-4">
                    <button
                      className={`flex items-center justify-center w-full bg-[#F15D18] hover:bg-[#E54D08] text-white font-medium py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-[#F15D18] focus:ring-opacity-50 ${
                        approvingAction ? "opacity-70 cursor-not-allowed" : ""
                      }`}
                      onClick={() => handleAction("approve")}
                      disabled={approvingAction || decliningAction}
                    >
                      {approvingAction ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Processing...
                        </span>
                      ) : (
                        <>
                          <svg
                            className="w-5 h-5 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M5 13l4 4L19 7"
                            ></path>
                          </svg>
                          Approve
                        </>
                      )}
                    </button>
                    <button
                      className={`flex items-center justify-center w-full border border-red-500 text-red-500 hover:bg-red-50 font-medium py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 ${
                        decliningAction ? "opacity-70 cursor-not-allowed" : ""
                      }`}
                      onClick={() => handleAction("decline")}
                      disabled={approvingAction || decliningAction}
                    >
                      {decliningAction ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-500"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Processing...
                        </span>
                      ) : (
                        <>
                          <svg
                            className="w-5 h-5 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M6 18L18 6M6 6l12 12"
                            ></path>
                          </svg>
                          Decline
                        </>
                      )}
                    </button>
                  </div>
                )}

                {fundRequest.status === "approved" && (
                  <div
                    className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mt-4"
                    role="alert"
                  >
                    <p>This fund request has been approved.</p>
                  </div>
                )}

                {fundRequest.status === "rejected" && (
                  <div
                    className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mt-4"
                    role="alert"
                  >
                    <p>This fund request has been declined.</p>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="w-1/3 flex flex-col justify-center bg-[#FBFAF9] p-12">
            <div className="text-center">
              <img
                src={paymentLogo}
                alt="Help Illustration"
                className="mx-auto mb-16 w-[290px] h-[278px]"
              />
              <div className="flex flex-col gap-10 justify-center">
                <p className="text-center w-full text-[#919191] nunito-regular text-text">
                  {actionComplete
                    ? "Thank you for reviewing this request. Please sign in to access your dashboard."
                    : "Transfer funds and empower your team."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default FundRequest;
